
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Crown, Palette, Globe, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface EnterpriseWhiteLabelStatusProps {
  subscriptionType: string;
  hasActiveSubscription: boolean;
}

export const EnterpriseWhiteLabelStatus = ({ 
  subscriptionType, 
  hasActiveSubscription 
}: EnterpriseWhiteLabelStatusProps) => {
  const navigate = useNavigate();
  
  const isWhiteLabelEnabled = hasActiveSubscription && 
    (subscriptionType === 'enterprise' || subscriptionType === 'white_label');

  const getStatusBadge = () => {
    if (isWhiteLabelEnabled) {
      return (
        <Badge className="bg-purple-100 text-purple-800">
          <Crown className="h-3 w-3 mr-1" />
          White-Label Active
        </Badge>
      );
    }
    
    if (hasActiveSubscription) {
      return (
        <Badge variant="secondary">
          Upgrade to Enterprise
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline">
        Subscription Required
      </Badge>
    );
  };

  const getFeatureList = () => {
    const features = [
      { icon: <Palette className="h-4 w-4" />, text: "Custom branding & colors" },
      { icon: <Globe className="h-4 w-4" />, text: "Custom domain setup" },
      { icon: <Settings className="h-4 w-4" />, text: "Logo customization" },
      { icon: <Crown className="h-4 w-4" />, text: "Remove CSO Agent branding" }
    ];

    return features.map((feature, index) => (
      <div key={index} className="flex items-center gap-2 text-sm">
        <span className={isWhiteLabelEnabled ? "text-purple-600" : "text-gray-400"}>
          {feature.icon}
        </span>
        <span className={isWhiteLabelEnabled ? "text-gray-800" : "text-gray-500"}>
          {feature.text}
        </span>
      </div>
    ));
  };

  return (
    <Card className={isWhiteLabelEnabled ? "border-purple-200 bg-purple-50" : "border-gray-200"}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            White-Label Features
          </CardTitle>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {getFeatureList()}
        </div>
        
        <div className="pt-4 border-t">
          {!isWhiteLabelEnabled ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Unlock white-label capabilities with an Enterprise subscription.
              </p>
              <Button 
                onClick={() => navigate('/plan-selection')}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Upgrade to Enterprise
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-purple-700">
                Your Enterprise subscription includes full white-label capabilities.
              </p>
              <Button 
                onClick={() => navigate('/organization')}
                variant="outline"
                className="w-full border-purple-600 text-purple-600 hover:bg-purple-50"
              >
                Configure White-Label Settings
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
